import { useEffect, useMemo, FC } from 'react';
import {
  Box,
  Button,
  Grid,
  GridItem,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import {
  postSongAudioEffect,
  uploadAudioEffect,
} from 'app/store/SongList/effects';
import {
  ChakraSelectField,
  InputField,
  ChakraFormSwitch,
} from 'app/components/Core';
import {
  getDefaultValues,
  getValidationSchema,
  transformAudioTypesToOptions,
  formatAudioFileMetaData,
} from './utils';
import { AudioAddDialogProps, AudioFormData } from './types';

export const AudioAddDialog: FC<AudioAddDialogProps> = ({
  open,
  handleClose,
  audioFiles,
  audioFormValidation,
  audioTypes,
  setAudioFiles,
  dispatch,
}) => {
  const audioTypesOptions = useMemo(
    () => transformAudioTypesToOptions(audioTypes),
    [audioTypes]
  );
  const multipleFiles = audioFiles && audioFiles.length > 1;
  const {
    handleSubmit,
    control,
    register,
    reset,
    formState: { isSubmitting, isValid, errors },
  } = useForm<AudioFormData>({
    defaultValues: getDefaultValues(
      audioFormValidation,
      audioTypesOptions,
      audioFiles
    ),
    mode: 'all',
    resolver: yupResolver(getValidationSchema(audioFormValidation)),
  });

  const onSubmit = async (data: AudioFormData) => {
    if (!audioFiles) return;
    try {
      const { filesMeta } = data;
      const uploadDetails = await Promise.allSettled(
        filesMeta.map(async (meta, index) =>
          dispatch(
            postSongAudioEffect({
              audio: {
                fileMeta: formatAudioFileMetaData(meta),
                file: audioFiles[index],
              },
            })
          ).unwrap()
        )
      );
      if (uploadDetails) {
        handleClose();
        uploadDetails.forEach((details, index) => {
          if (details.status === 'fulfilled' && details.value) {
            dispatch(
              uploadAudioEffect({
                audioFile: audioFiles[index],
                audio: details.value,
              })
            );
          }
        });
      }
    } catch (e) {
      // Handle error
    }
  };
  useEffect(() => {
    if (audioFiles) {
      reset(
        getDefaultValues(audioFormValidation, audioTypesOptions, audioFiles)
      );
    }
  }, [audioFormValidation, audioFiles, reset, audioTypesOptions]);
  return (
    <Modal
      size="md"
      isOpen={open}
      onClose={handleClose}
      aria-labelledby="Audio file"
      aria-describedby="Upload audio file"
      scrollBehavior="inside"
      onCloseComplete={() => setAudioFiles(undefined)}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader data-testid="dialog-title" title="Upload audio">
          Upload audio
        </ModalHeader>
        <ModalBody data-testid="dialog-content" pb={6}>
          {audioFiles && (
            <VStack gap={4}>
              {Array.from(audioFiles).map((file, i) => (
                <Box w="full" key={`meta-fields-${file.name}`}>
                  {multipleFiles && (
                    <Text
                      color="textSubdued"
                      fontWeight={500}
                      flexShrink={0}
                      pb={1}
                    >
                      File {i + 1}
                    </Text>
                  )}
                  <Grid columnGap={4} templateColumns="repeat(2, 1fr)">
                    <GridItem colSpan={2}>
                      <InputField
                        label="Title"
                        isInvalid={Boolean(errors.filesMeta?.[i]?.title)}
                        errorMessage={errors.filesMeta?.[i]?.title?.message}
                        data-testid={`audio-title-field-${i}`}
                        {...register(
                          `filesMeta.${i}.title` as `filesMeta.${number}.title`
                        )}
                      />
                    </GridItem>
                    <GridItem>
                      <ChakraSelectField
                        control={control}
                        name={
                          `filesMeta.${i}.type` as `filesMeta.${number}.type`
                        }
                        label="Type"
                        options={audioTypesOptions}
                        dataTestId={`audio-type-field-${i}`}
                        optionDataTestId="audio-type-field-option"
                        menuPosition="fixed"
                      />
                    </GridItem>
                    <GridItem>
                      <ChakraFormSwitch
                        control={control}
                        data-testid={`audio-private-checkmark-${i}`}
                        label="Private file"
                        name={
                          `filesMeta.${i}.isPrivate` as `filesMeta.${number}.isPrivate`
                        }
                        labelProps={{
                          color: 'textBase !important',
                        }}
                      />
                    </GridItem>
                    <GridItem colSpan={2}>
                      <InputField
                        label="Note"
                        isInvalid={Boolean(errors.filesMeta?.[i]?.remark)}
                        errorMessage={errors.filesMeta?.[i]?.remark?.message}
                        data-testid={`audio-note-field-${i}`}
                        {...register(
                          `filesMeta.${i}.remark` as `filesMeta.${number}.remark`
                        )}
                      />
                    </GridItem>
                  </Grid>
                </Box>
              ))}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter data-testid="dialog-actions">
          <Button onClick={handleClose} variant="secondary">
            Cancel
          </Button>
          <Button
            ml={2}
            isDisabled={isSubmitting || !isValid}
            onClick={handleSubmit(onSubmit)}
            data-testid="AudioAddDialog-upload-btn"
          >
            Upload
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
